// Batched, non-blocking event queue. Flushes when it reaches 10 events or after
// 5s, and on tab-hide / pagehide. A failed send is re-queued at the front and
// retried on the next flush; the buffer is bounded so a prolonged outage can't
// grow memory without limit (oldest events are dropped past the cap).

import { send } from './transport';
import type { OutgoingEvent } from './types';

const FLUSH_AT = 10;
const FLUSH_MS = 5000;
const MAX_BATCH = 50; // server per-call cap
const MAX_BUFFER = 200; // memory bound if the endpoint is unreachable

let buffer: OutgoingEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

export function enqueue(e: OutgoingEvent): void {
  buffer.push(e);
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
  if (buffer.length >= FLUSH_AT) {
    void flush();
    return;
  }
  if (timer === null) timer = setTimeout(() => void flush(), FLUSH_MS);
}

export async function flush(): Promise<void> {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer.slice(0, MAX_BATCH);
  buffer = buffer.slice(MAX_BATCH);
  const ok = await send(batch);
  // Re-queue a failed batch at the front, keeping the NEWEST MAX_BUFFER events
  // (same drop-oldest policy as enqueue) so the two caps don't fight.
  if (!ok) buffer = [...batch, ...buffer].slice(-MAX_BUFFER);
  // Self-schedule the retry / drain-remainder: without this, events stranded by
  // a failed or partial (>MAX_BATCH) flush would sit until the next enqueue.
  if (buffer.length > 0 && timer === null) timer = setTimeout(() => void flush(), FLUSH_MS);
}

let listenersBound = false;
export function bindFlushListeners(): void {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  window.addEventListener('pagehide', () => void flush());
}

/**
 * Test-only: clear the buffer and any pending timer.
 * @internal
 */
export function __resetQueue(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  buffer = [];
}
